import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import debounce from 'lodash/debounce';
import * as helpers from '@turf/helpers';

import {
  getMapWaterPolygons,
  getMapWaterUnionPolygon,
} from '../utils/helpers/mapWaterLayerUtils';
import {
  checkZoomEditable,
  checkZoomInteractive,
  executeWhenMapLoaded,
} from '../utils/helpers/utils';
import { WATERBODIES_LAYER_ID } from '../components/layers/waterbodies-transparent-layer/WaterbodiesTransparentLayer';

import { useGlMap } from './context';

const context = React.createContext<helpers.Feature<
  helpers.Polygon,
  helpers.Properties
> | null>(null);

const SAMPLING_RATE = 200;

const MapWaterPolygonProvider = ({ children }: { children: ReactNode }) => {
  const map = useGlMap();
  const unionMapWaterPolygonsWorker: Worker | null = useMemo(() => {
    if (!window.Worker) return null;
    return new Worker(
      new URL('../../workers/unionMapWaterPolygonsWorker.ts', import.meta.url),
    );
  }, []);
  // null means no layer or initial value, yet still may be restricted
  const [waterPolygon, setWaterPolygon] = useState<helpers.Feature<
    helpers.Polygon,
    helpers.Properties
  > | null>(null);

  useEffect(() => {
    if (unionMapWaterPolygonsWorker) {
      unionMapWaterPolygonsWorker.onmessage = (
        e: MessageEvent<helpers.Feature<helpers.Polygon, helpers.Properties>>,
      ) => {
        setWaterPolygon(e.data);
      };
    }
  }, [unionMapWaterPolygonsWorker]);

  useEffect(() => {
    if (!map) {
      return;
    }
    const handleMapWaterPolygonsUnion = (
      features: helpers.Feature<helpers.Polygon, helpers.Properties>[] | null,
    ) => {
      if (unionMapWaterPolygonsWorker) {
        unionMapWaterPolygonsWorker.postMessage(features);
      }
    };

    const handleMapMove = debounce(() => {
      if (!checkZoomEditable(map.getZoom())) {
        return;
      }

      if (unionMapWaterPolygonsWorker) {
        const waterPolygons = getMapWaterPolygons(map);
        handleMapWaterPolygonsUnion(waterPolygons);
        return;
      }
      setWaterPolygon(getMapWaterUnionPolygon(map));
    }, SAMPLING_RATE);

    const handleMapSourceDataForWaterbodiesLayer = debounce(() => {
      if (!checkZoomInteractive(map.getZoom())) {
        return;
      }

      if (unionMapWaterPolygonsWorker) {
        const waterPolygons = getMapWaterPolygons(map);
        handleMapWaterPolygonsUnion(waterPolygons);
        return;
      }
      setWaterPolygon(getMapWaterUnionPolygon(map));
    }, SAMPLING_RATE);

    const handleSourceData = (event) => {
      if (event.sourceId === WATERBODIES_LAYER_ID) {
        handleMapSourceDataForWaterbodiesLayer();
      }
    };

    map.on('move', handleMapMove);
    map.on('sourcedata', handleSourceData);
    executeWhenMapLoaded(map, handleMapSourceDataForWaterbodiesLayer);

    return () => {
      map.off('move', handleMapMove);
      map.off('sourcedata', handleSourceData);
      if (unionMapWaterPolygonsWorker) {
        unionMapWaterPolygonsWorker.terminate();
      }
    };
  }, [map, unionMapWaterPolygonsWorker]);

  return <context.Provider value={waterPolygon}>{children}</context.Provider>;
};

const useMapWaterPolygon = () => {
  return useContext(context);
};

const { Consumer: MapWaterPolygonConsumer } = context;

export { MapWaterPolygonConsumer, MapWaterPolygonProvider, useMapWaterPolygon };
